import { Flex } from 'antd';
import { z } from 'zod';

import { useNavigate } from '@suntex.core/nav';
import { GoalsIndexChart } from '@suntex.fim/assessment';
import {
  $SpecialGradeLevel,
  EntityLevel,
  SpecialGradeLevel,
} from '@suntex.fim/enums';
import { getFlags } from '@suntex.fim/flags';
import {
  PageMetadata,
  useFramework,
  usePersistedState,
} from '@suntex.fim/framework';
import { HubSettings, gradeLevelToDefaultPhase } from '@suntex.fim/hub';
import { Card } from '@suntex.fim/ui';

import { usePlayer } from '../hooks';
import { NO_FOCUS, useFocus } from '../hooks/useFocus';
import { getModuleTagsForGradeLevels } from '../utils/focus';

import { FocusSelector } from './FocusSelector';
import { Hub } from './Hub';
import {
  ANIMATION_SPEED_MAX,
  ANIMATION_SPEED_MIN,
  HubSettingsMenu,
} from './HubSettingsMenu';

const hubSettingsSchema = z.object({
  /** Path the hub opens on when there is no URL hash to restore. */
  defaultPath: z.string().catch('/stages-of-learning'),
  catalogView: z.boolean().catch(false),
  alwaysShowLabels: z.boolean().catch(false),
  animationSpeed: z
    .number()
    .min(ANIMATION_SPEED_MIN)
    .max(ANIMATION_SPEED_MAX)

    .catch(1),
  disableAnimation: z.boolean().catch(false),
  flatten: z.boolean().catch(false),
});

const DEFAULT_HUB_SETTINGS: HubSettings = hubSettingsSchema.parse({});

export function DashboardMain({
  onGoalsIndexChartClick,
  settings,
}: {
  onGoalsIndexChartClick?: () => void;
  settings?: HubSettings;
}) {
  const flags = getFlags();
  const { calendar } = useFramework();
  const { playerId, isPreview, playerInfo } = usePlayer();
  const navigate = useNavigate();

  const [persistedHubSettings, setPersistedHubSettings] =
    usePersistedState<HubSettings>('hubSettings', hubSettingsSchema);
  const hubSettings = settings ?? persistedHubSettings;
  // If settings are passed in as props, we don't want to allow the user to change them via the HubSettingsMenu
  const setHubSettings = settings ? undefined : setPersistedHubSettings;

  const { focus, setFocus, focusOptions } = useFocus({ playerInfo });
  const goalsIndexPhase =
    focus !== NO_FOCUS ? gradeLevelToDefaultPhase(focus) : null;

  return (
    <Flex vertical className="main-content" gap=".5em">
      <PageMetadata title="Player Dashboard" />

      {flags.player.focusSelector && playerInfo && (
        <>
          <Flex className="focus-mode-select" align="center" justify="center">
            <FocusSelector
              focus={focus}
              setFocus={setFocus}
              focusOptions={focusOptions}
            />
          </Flex>
          <hr />
        </>
      )}

      <Flex
        className="quick-links quick-links-main"
        justify="space-between"
        align="center"
        gap={10}
      >
        <div>
          {isPreview && (
            <span>
              <b>Preview mode</b> (No stickers will be earned)
            </span>
          )}
        </div>

        {setHubSettings && (
          <HubSettingsMenu
            settings={hubSettings}
            defaultSettings={DEFAULT_HUB_SETTINGS}
            applySettings={(settings) => {
              const reload = hubSettings.defaultPath !== settings.defaultPath;
              setHubSettings(settings);
              if (reload) {
                navigate(`#${settings.defaultPath}`);
              }
            }}
          />
        )}
      </Flex>

      {/* Hub */}
      <Hub
        {...{
          focus: getModuleTagsForGradeLevels(
            focus !== NO_FOCUS ? [focus] : undefined,
          ),
          gradeLevel: focus !== NO_FOCUS ? focus : undefined,
          settings: hubSettings,
        }}
      />

      {/* Goals index */}
      <Flex vertical className="tiles-container">
        {!isPreview && playerId && playerInfo && goalsIndexPhase && (
          <Card>
            <GoalsIndexChart
              id={playerId}
              level={EntityLevel.Player}
              year={playerInfo.team?.year || calendar.getCurrentSeason()}
              phase={goalsIndexPhase}
              grade={
                playerInfo.team?.grade
                  ? $SpecialGradeLevel.from(playerInfo.team.grade)
                  : SpecialGradeLevel.Null
              }
              showLegend={true}
              onChartClick={onGoalsIndexChartClick}
            />
          </Card>
        )}
      </Flex>
    </Flex>
  );
}
